// File: src/Hooks/GetSupportModalApi/useGetTicketDetails.ts
import {useQuery, useQueryClient} from '@tanstack/react-query';
import ChessRestClient from '@/Api/ChessRestClient';
import {getLocalStorage} from '@/Utils/CommonUtils';

export interface Message {
  id: string;
  text: string;
  from: 'left' | 'right';
  user: string;
}

export interface TicketDetails {
  id: number;
  title: string;
  status: 'Resolved' | 'In progress' | 'Not Seen';
  category: string;
  created_at: string;
  last_updated: string;
  user_unread_count: number;
  messages: Message[];
}

interface ApiResponse {
  status: 'success' | 'error';
  data: TicketDetails;
}
interface UseGetTicketDetailsProps {
  id?: number | string;
  enabled?: boolean;
  handleOnSuccess?: (data: TicketDetails) => void;
  handleOnError?: (error: unknown) => void;
}
export function useGetTicketDetails({
  id,
  enabled = true,
  handleOnSuccess,
  handleOnError,
}: UseGetTicketDetailsProps) {
  const queryClient = useQueryClient(); // 👈 add this
  const query = useQuery({
    enabled: !!id && enabled,
    queryKey: ['ticketDetails', id],
    queryFn: async (): Promise<TicketDetails> => {
      const token = await getLocalStorage('access_token');
      const response = await ChessRestClient.get<ApiResponse>(
        `/user_raised_issues/ticket/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        },
      );

      if (response.data.status !== 'success') {
        throw new Error('Failed to fetch ticket details');
      }

      return response.data.data;
    },
    // Added handlers
    onSuccess: data => {
      handleOnSuccess?.(data);
    },
    onError: error => {
      handleOnError?.(error);
    },
    // staleTime: 1000 * 60 * 2,
    // refetchOnWindowFocus: false,
  });
  // 👇 Custom helper like your useGetMessagesApi
  const refetchTicket = (ticketId: number | string) =>
    queryClient.refetchQueries({
      queryKey: ['ticketDetails', ticketId],
      exact: true,
    });

  return {
    ...query,
    refetchTicket, // 👈 return custom refetch method
  };
}
